import {Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {first, takeUntil} from 'rxjs/operators';
import {MatSelectChange} from '@angular/material/select';
import {InsuranceCompany} from '../../interface/insurance-company';
import {IC_BALCIA, IC_PZU, InsuranceCompanyService} from '../../services/insurance-company.service';
import {CountedAndPaginated} from '../../interface/response/counted-and-paginated';
import {Dictionary} from '../../interface/dictionary';
import {interval, ReplaySubject} from 'rxjs';

@Component({
  selector: 'app-dict-insurance-companies',
  templateUrl: './dict-insurance-companies.component.html',
  styleUrls: ['./dict-insurance-companies.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictInsuranceCompaniesComponent)}
  ]
})
export class DictInsuranceCompaniesComponent implements OnInit, OnDestroy, ControlValueAccessor, OnChanges {
  @Input() label: string;
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() companyId?: ReplaySubject<number>;
  @Input() profit = false;
  @Input() hideBlocked = false;
  @Input() hideInactive = false;
  @Input() hideChildren = false;
  @Input() forceShowPzu = false;
  @Input() forceHidePzu = false;
  @Input() forceShowBalcia = false;
  @Input() allSelect = false;
  @Input() disable = false;
  @Input() onlyKeys?: string[] = null;
  @Input() skipKeys?: string[] = null;
  @Input() forceShowInsuranceCompanyIds: number[];
  @Output() selectedInsuranceCompany: EventEmitter<InsuranceCompany> = new EventEmitter<InsuranceCompany>();
  @Output() selectedInsuranceCompanies: EventEmitter<InsuranceCompany[]> = new EventEmitter<InsuranceCompany[]>();

  externalControl = true;
  disabled = false;
  items: InsuranceCompany[] = [];
  filteredItems: InsuranceCompany[] = [];
  filterControl: FormControl = new FormControl('');
  value?: number = null;
  companyIdNumber?: number;
  allSelection = false;
  values = [];

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(private insuranceCompanyService: InsuranceCompanyService) { }

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    this.insuranceCompanyService.loadList(0, 1000, {});
    if (this.companyId) {
      this.companyId
        .pipe(takeUntil(this.destroyed$))
        .subscribe((companyId?: number) => {
          this.loadItems(companyId);
          this.companyIdNumber = companyId;
        });
    } else if (this.profit){
      this.loadItems(null, this.profit);
    } else {
      this.loadItems();
    }

    this.filterControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val?: string) => {
        this.filteredItems = this.items;

        if (!val) {
          return;
        }

        this.filteredItems = this.filteredItems.filter((insuranceCompany: InsuranceCompany) => {
          return insuranceCompany.name.toUpperCase().includes(val.toUpperCase())
            || insuranceCompany.fullName.toUpperCase().includes(val.toUpperCase());
        });
      });

    if (this.control === null) {
      this.control = new FormControl({ value: '', disabled: this.disable });
      this.externalControl = false;
    } else if (this.disable) {
      this.control.disable();
    }
  }

  ngOnChanges(): void {
    this.loadItems(this.companyIdNumber);
  }

  loadItems(companyId?: number, profit?: boolean): void {
    this.insuranceCompanyService.getInsuranceCompaniesPageSubject()
      .pipe(first())
      .subscribe((result: CountedAndPaginated<InsuranceCompany>) => {
        this.filteredItems = result.items.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });

        if (companyId) {
          this.filteredItems = this.filteredItems.filter((insuranceCompany: InsuranceCompany) => {
            if (this.forceShowPzu && insuranceCompany.key === IC_PZU) {
              return true;
            }

            if (this.forceShowBalcia && insuranceCompany.key === IC_BALCIA) {
              return true;
            }

            if (this.forceShowInsuranceCompanyIds && this.forceShowInsuranceCompanyIds.includes(insuranceCompany.id)) {
              return true;
            }

            let matches = false;
            insuranceCompany.companies.forEach((company: Dictionary) => {
              if (company.id === companyId) {
                matches = true;
              }
            });

            return matches;
          });
        }
        if (this.profit) {
          this.filteredItems = this.filteredItems.filter((insuranceCompany: InsuranceCompany) => {
            return insuranceCompany.profitImport;
          });
        }

        if (this.hideBlocked) {
          this.filteredItems = this.filteredItems.filter((insuranceCompany: InsuranceCompany) => {
            return !insuranceCompany.blockRegistrations;
          });
        }

        if (this.hideInactive) {
          this.filteredItems = this.filteredItems.filter((insuranceCompany: InsuranceCompany) => {
            return !insuranceCompany.inactive;
          });
        }

        if (this.hideChildren) {
          this.filteredItems = this.filteredItems.filter((insuranceCompany: InsuranceCompany) => {
            return !insuranceCompany.parent || insuranceCompany.parent.id === insuranceCompany.id;
          });
        }

        if (this.onlyKeys !== null) {
          this.filteredItems = this.filteredItems.filter((insuranceCompany: InsuranceCompany) => {
            return this.onlyKeys.includes(insuranceCompany.key);
          });
        }

        if (this.skipKeys !== null) {
          this.filteredItems = this.filteredItems.filter((insuranceCompany: InsuranceCompany) => {
            return !this.skipKeys.includes(insuranceCompany.key);
          });
        }

        if (this.forceHidePzu) {
          this.filteredItems = this.filteredItems.filter((insuranceCompany: InsuranceCompany) => {
            return insuranceCompany.key !== IC_PZU;
          });
        }

        this.items = this.filteredItems;
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }

    this.emitSelected(value);
    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.emitSelected(e.value);

    this.value = e.value;
    this.onChange(this.value);
  }

  emitSelected(value?: any): void {
    this.insuranceCompanyService.getInsuranceCompaniesPageSubject()
      .pipe(first())
      .subscribe((result: CountedAndPaginated<InsuranceCompany>) => {
        if (this.multiple) {
          const insuranceCompanies = [];

          result.items.forEach((insuranceCompany: InsuranceCompany) => {
            if (value?.includes(insuranceCompany.id)) {
              insuranceCompanies.push(insuranceCompany);
            }
          });

          this.selectedInsuranceCompanies.emit(insuranceCompanies);
        } else {
          result.items.forEach((insuranceCompany: InsuranceCompany) => {
            if (insuranceCompany.id === value) {
              this.selectedInsuranceCompany.emit(insuranceCompany);
            }
          });
        }
      });
  }

  clearValue(): void {
    this.selectedInsuranceCompanies.emit([]);
    this.selectedInsuranceCompany.emit(null);
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
    this.values = [];
  }
  toggleAllSelection( change: any): void {
    this.clearValue();
          const insuranceCompanies = [];
        if (!this.allSelection) {
          this.allSelection = true;
          this.filteredItems.forEach((insuranceCompany: InsuranceCompany) => {
              insuranceCompanies.push(insuranceCompany);
              this.values.push(insuranceCompany.id);
          });
          this.control.setValue(this.values);
          this.selectedInsuranceCompanies.emit(insuranceCompanies);
          this.writeValue(insuranceCompanies);
        } else {
          this.allSelection = false;
          this.control.setValue([]);
        }

  }

  isChecked(): boolean {
    return this.control.value && this.values.length
      && this.control.value.length === this.values.length;
  }

  isIndeterminate(): boolean {
    return this.control.value && this.values.length && this.control.value.length
      && this.control.value.length < this.values.length;
  }
}
